import * as React from 'react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getRepoSpaceName } from '@basehub/data/replicache/index'
import { getURL } from '@basehub/shared-constants'
import { getClerkSessionFromCookie } from '~/replicache/frontend/get-clerk-session-from-cookie'
import { IndexedDocument } from '@basehub/data/indexing/index'
import { SearchResponseHit } from '@basehub/typesense-client'

interface SearchParams {
  q: string
  by?: string[]
  limit?: number
  rootBlockId?: string
  byType?: string[]
}

export type SearchResponseBlock = Omit<
  SearchResponseHit<IndexedDocument>,
  'highlight'
> & {
  blockId: string
  highlight?: Array<{
    by: string
    snippet: string
    matched_tokens: string[]
  }>
  highlights?: Array<{
    field: string
    snippet: string
    matched_tokens: string[]
  }>
}

interface UseSearchRepoReturn {
  search: (params: SearchParams) => Promise<SearchResponseBlock[]>
  isLoading: boolean
  error: string | null
  results: SearchResponseBlock[]
  clearResults: () => void
}

export const useSearchRepo = (): UseSearchRepoReturn => {
  const { data: repo } = useRepoReplicache()
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [results, setResults] = React.useState<SearchResponseBlock[]>([])

  const spaceId = React.useMemo(() => {
    return repo?.team && repo?.repoId
      ? getRepoSpaceName(repo.team.id, repo.repoId)
      : null
  }, [repo?.team, repo?.repoId])

  const defaultRootBlockId = useWorkingTreeStore((s) => s.rootBlockId) ?? ''

  // Debounced search to optimize rapid interactions
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const search = React.useCallback(
    async (params: SearchParams): Promise<SearchResponseBlock[]> => {
      if (!params.q.trim()) {
        setResults([])
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current)
        }
        return []
      }

      if (!spaceId) {
        setError('Repository context not ready')
        return []
      }

      // Use provided rootBlockId or get from working tree
      const rootBlockId = params.rootBlockId || defaultRootBlockId
      if (!rootBlockId) {
        setError('Root block ID not available')
        return []
      }

      // Clear previous timeout for rapid interactions
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      return new Promise((resolve, reject) => {
        searchTimeoutRef.current = setTimeout(async () => {
          setIsLoading(true)
          setError(null)

          try {
            const results = await fetchSearchResults({
              query: params.q,
              spaceId,
              rootBlockId,
              by: params.by || ['content', 'title'],
              limit: params.limit || 10,
              searchByNearestParent: true,
              byType: params.byType,
            })

            setResults(results)
            resolve(results)
          } catch (err: any) {
            const errorMessage = err.message || 'Search failed'
            setError(errorMessage)
            console.error('Search error:', err)
            reject(err)
          } finally {
            setIsLoading(false)
          }
        }, 150) // 150ms debounce for rapid interactions
      })
    },
    [spaceId, defaultRootBlockId]
  )

  const clearResults = React.useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return {
    search,
    isLoading,
    error,
    results,
    clearResults,
  }
}

export const fetchSearchResults = async ({
  query,
  spaceId,
  rootBlockId,
  by,
  limit,
  searchByNearestParent = false,
  byType,
}: {
  query: string
  spaceId: string
  rootBlockId: string
  by: string[]
  limit: number
  byType?: string[]
  searchByNearestParent?: boolean
}) => {
  const response = await fetch(
    getURL(
      'pump',
      process.env.NEXT_PUBLIC_ENVIRONMENT as 'production' | undefined
    ) + '/search',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-basehub-session': getClerkSessionFromCookie() || '',
      },
      body: JSON.stringify({
        q: query,
        spaceID: spaceId,
        rootBlockId,
        by: by || ['content', 'title'],
        limit: limit || 10,
        byType,
        searchByNearestParent,
      }),
    }
  )

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      error?: string
    }
    throw new Error(
      errorData.error || `Search failed with status ${response.status}`
    )
  }

  const data = (await response.json()) as
    | { results?: SearchResponseBlock[] }
    | SearchResponseBlock[]

  const validResults = Array.isArray(data)
    ? data
    : data.results?.map((data) => data) || []

  return validResults
}
